/**
 * 当打开城市规划面板时，鼠标移动到地图上时，
 * 显示相应的 boundary与popup信息
 * 
 * @Author: Focci
 * @Date: 2023-08-05 08:36:31
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-05 08:36:31
 */

import useAbortController from '@hook/useAbortController'
import { fetchGet } from '@lib/fetch/client'
import { debounce, forEach, isArray, map, pick } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { useMap, Popup } from 'react-map-gl'
import Boundary from '../Boundary'

export default function UnitaryHoverPopup({ layers }) {
  const { current: mapbox } = useMap()
  const { getSignal } = useAbortController()

  const [lngLat, setLngLat] = useState(null)
  const [text, setText] = useState('')
  const [boundary, setBoundary] = useState(null)

  /**
   * 关闭 popup
   */
  const handlePopupClose = useCallback(() => {
    setBoundary(null)
    setText('')
  }, [])

  /** 
   * 获取数据
   */
  const fetchData = useCallback((param) => {
    const { signal } = getSignal()
    return fetchGet('unitary-plan-locate', param, { signal })
  }, [getSignal])

  /**
   * 点击地图，获取polygon
   */
  const handleClick = useCallback(({ lng, lat, ...opt }) => {
    setLngLat({ lng, lat })
    fetchData({ lng, lat, ...opt }).then((res) => {
      const { boundary: b, zone } = res || {}
      
      isArray(b?.coordinates) && setBoundary([b.coordinates || []])
      zone && setText(zone)
    })
  }, [fetchData])

  useEffect(() => {
    const deboundeHandle = debounce(handleClick, 100)
    const handleDefault = (e) => deboundeHandle(pick(e.lngLat, ['lng', 'lat']))

    const hasLayer = layers?.length > 0
    const handles = map(hasLayer ? layers : [], (t) => ({
      layer: t.layer,
      handle: (e) => {
        const { lng, lat } = e.lngLat
        const value = e.features?.[0]?.properties?.[t.prop]
        value && deboundeHandle({ value, lng, lat, layer: t.layer })
      }
    }))
    
    if(hasLayer) {
      forEach(handles, (t) => mapbox.on('click', t.layer, t.handle))
    } else {
      mapbox.on('click', handleDefault)
    }

    return () => {
      if(hasLayer) {
        forEach(handles, (t) => mapbox.off('click', t.layer, t.handle))
      } else {
        mapbox.off('click', handleDefault)
      }
    }
  }, [handleClick, layers, mapbox])
  
  return (
    <>
      {isArray(boundary) && map(boundary, (item) => (
        <Boundary data={item} translate={false} />
      ))}
      {text && lngLat && (
        <Popup
          longitude={lngLat.lng} 
          latitude={lngLat.lat}
          anchor="bottom"
          onClose={handlePopupClose}
          style={{ zIndex: 8 }}
        >
          {text}
        </Popup>
      )}
    </>
  )
}
